import { useState } from "react";
import { ArrowLeft, Sparkles, FileText, Pill, HeartPulse, Clock, AlertTriangle, ShieldAlert, Receipt } from "lucide-react";
import { UnifiedRecord, AppView } from "../../types";
import { useSpeech } from "../../hooks/useSpeech";

interface Props {
  records: UnifiedRecord[];
  selectedPatientId: string | null;
  onNavigate: (view: AppView, params?: any) => void;
}

export function PatientIntelligence({ records, selectedPatientId, onNavigate }: Props) {
  const { speak, isSpeaking, stop } = useSpeech();
  const [showWhyModal, setShowWhyModal] = useState<boolean>(false);

  const patient = records.find(r => r.patient_id === selectedPatientId);

  if (!patient) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <AlertTriangle className="w-12 h-12 text-amber-500 mb-4" />
        <h2 className="text-xl text-white">Patient Not Found</h2>
        <button onClick={() => onNavigate("doctor-dash")} className="mt-4 text-indigo-400 hover:underline">
          Return to Command Center
        </button>
      </div>
    );
  }

  const pendingLabs = patient.lab_results.filter(l => l.result_status !== "Completed");
  const statLabs = patient.lab_results.filter(l => l.priority === "STAT" && l.result_status !== "Completed");
  const isFlagged = patient.has_conflict || statLabs.length > 0;

  // Generate TTS Summary
  const handleExplain = () => {
    if (isSpeaking) {
      stop();
      return;
    }
    
    let summary = `Patient ${patient.patient_name || patient.patient_id} is currently in ${patient.ward || 'an unknown ward'}. `;
    
    if (patient.his_clinical_status) {
      summary += `Current status is ${patient.his_clinical_status}. `;
    }

    if (patient.has_conflict) {
      summary += `An operational conflict has been detected requiring your review. `;
    }

    if (pendingLabs.length > 0) {
      summary += `There are ${pendingLabs.length} pending laboratory investigations. `;
      if (statLabs.length > 0) {
        summary += `${statLabs.length} of these are marked STAT. `;
      }
    }

    summary += "Please note that clinical measurements like vitals and medications are not available in the current dataset. Please refer to original clinical records.";

    speak(summary);
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 border-b border-indigo-500/20 pb-4">
        <button onClick={() => onNavigate("doctor-dash")} className="p-2 bg-white/5 rounded-xl text-gray-400 hover:text-white hover:bg-white/10 transition-all">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            Patient Intelligence 
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 tracking-widest uppercase">Clinical Copilot</span>
          </h2>
          <p className="text-gray-400 text-sm">Deep-dive clinical and operational review</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Left Column: Summary & Explain */}
        <div className="lg:col-span-1 space-y-6">
          <div className="rounded-3xl border border-indigo-500/30 bg-gradient-to-b from-[#040d1a] to-indigo-950/20 p-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/10 blur-[50px] rounded-full pointer-events-none" />
            
            <div className="mb-6 relative z-10">
              <h3 className="text-3xl font-bold text-white mb-1">{patient.patient_name || "Unknown Patient"}</h3>
              <p className="text-indigo-300 font-mono text-sm">ID: {patient.patient_id}</p>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-6 relative z-10">
              <div className="bg-white/5 border border-white/5 rounded-xl p-3">
                <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider mb-1">Location</p>
                <p className="text-white text-sm font-medium">{patient.resolved_bed ? `${patient.ward} - ${patient.resolved_bed}` : "Pending Bed Allocation"}</p>
              </div>
              <div className="bg-white/5 border border-white/5 rounded-xl p-3">
                <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider mb-1">Status</p>
                <p className="text-white text-sm font-medium">{patient.his_clinical_status}</p>
              </div>
            </div>

            <button 
              onClick={handleExplain}
              className={`w-full py-4 rounded-2xl flex items-center justify-center gap-2 font-bold transition-all relative overflow-hidden group ${
                isSpeaking 
                  ? "bg-indigo-500 text-white shadow-[0_0_20px_rgba(99,102,241,0.6)]" 
                  : "bg-white/10 text-indigo-300 hover:bg-indigo-500/20 hover:text-indigo-200 border border-indigo-500/30"
              }`}
            >
              {isSpeaking && <div className="absolute inset-0 bg-white/20 animate-pulse pointer-events-none" />}
              <Sparkles className={`w-5 h-5 ${isSpeaking ? "animate-spin-slow" : "group-hover:animate-pulse"}`} />
              {isSpeaking ? "Stop Explanation" : "✨ Explain My Patient"}
            </button>
            <p className="text-center text-[10px] text-gray-500 mt-3">AI-generated summary. Verify against original records.</p>
          </div>

          {/* Indicators */}
          {isFlagged && (
            <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5">
              <h4 className="text-amber-400 font-bold flex items-center gap-2 mb-3">
                <ShieldAlert className="w-5 h-5" /> Configured Review Indicators
              </h4>
              <ul className="space-y-3">
                {statLabs.map((lab, i) => (
                  <li key={`stat-${i}`} className="bg-black/40 p-3 rounded-xl border border-white/5">
                    <p className="text-xs text-amber-200 font-medium">{lab.test_name}</p>
                    <p className="text-[10px] text-gray-400 mt-1">STAT lab pending. Delay in care risk.</p>
                    <button onClick={() => setShowWhyModal(true)} className="text-[10px] text-indigo-400 mt-2 hover:underline">Why was this flagged?</button>
                  </li>
                ))}
                {patient.has_conflict && (
                  <li className="bg-black/40 p-3 rounded-xl border border-white/5">
                    <p className="text-xs text-amber-200 font-medium">Operational Desync Detected</p>
                    <p className="text-[10px] text-gray-400 mt-1">Reconciliation engine found conflicting data sources.</p>
                    <button onClick={() => setShowWhyModal(true)} className="text-[10px] text-indigo-400 mt-2 hover:underline">Why was this flagged?</button>
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Right Column: Timeline & Clinical Data */}
        <div className="lg:col-span-2 space-y-4">
          
          {/* Missing Data Disclaimers (Strict Compliance) */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white/3 border border-white/5 rounded-2xl p-5 flex flex-col items-center justify-center text-center opacity-60">
              <HeartPulse className="w-6 h-6 text-rose-500/50 mb-2" />
              <h5 className="text-sm font-medium text-white mb-1">Vitals & Trends</h5>
              <p className="text-[10px] text-gray-500">Not available in current dataset.</p>
            </div>
            <div className="bg-white/3 border border-white/5 rounded-2xl p-5 flex flex-col items-center justify-center text-center opacity-60">
              <Pill className="w-6 h-6 text-emerald-500/50 mb-2" />
              <h5 className="text-sm font-medium text-white mb-1">Medication Safety</h5>
              <p className="text-[10px] text-gray-500">Not available in current dataset.</p>
            </div>
            <div className="bg-white/3 border border-white/5 rounded-2xl p-5 flex flex-col items-center justify-center text-center opacity-60">
              <FileText className="w-6 h-6 text-blue-500/50 mb-2" />
              <h5 className="text-sm font-medium text-white mb-1">Report Intelligence</h5>
              <p className="text-[10px] text-gray-500">Not available in current dataset.</p>
            </div>
          </div>

          {/* Billing & Charges Summary */}
          <div className="bg-[#040d1a]/80 backdrop-blur-xl border border-teal-500/20 rounded-3xl p-6">
            <h4 className="text-lg font-bold text-white flex items-center gap-2 mb-6">
              <Receipt className="w-5 h-5 text-teal-400" /> Billing & Charges Summary
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-white/5 border border-white/5 rounded-xl p-4 flex flex-col items-center justify-center text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Doctor</p>
                <p className="text-lg font-bold text-white">₹{patient.doctor_charge?.toLocaleString() || 0}</p>
                <p className="text-[10px] text-gray-500 mt-1">{patient.doctor_visits || 0} visits</p>
              </div>
              <div className="bg-white/5 border border-white/5 rounded-xl p-4 flex flex-col items-center justify-center text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Labs</p>
                <p className="text-lg font-bold text-white">₹{patient.lab_charge?.toLocaleString() || 0}</p>
              </div>
              <div className="bg-white/5 border border-white/5 rounded-xl p-4 flex flex-col items-center justify-center text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Room</p>
                <p className="text-lg font-bold text-white">₹{patient.room_charge?.toLocaleString() || 0}</p>
              </div>
              <div className="bg-white/5 border border-white/5 rounded-xl p-4 flex flex-col items-center justify-center text-center">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Surgery</p>
                <p className="text-lg font-bold text-white">₹{patient.surgery_charge?.toLocaleString() || 0}</p>
              </div>
            </div>
            <div className="flex items-center justify-between bg-teal-500/10 border border-teal-500/30 rounded-2xl p-5">
              <div>
                <p className="text-sm text-teal-400 font-bold uppercase tracking-wider">Total Amount Due</p>
                <p className="text-xs text-gray-400 mt-1">Includes all estimated charges</p>
              </div>
              <p className="text-3xl font-bold text-white">₹{patient.total_charge?.toLocaleString() || 0}</p>
            </div>
          </div>

          {/* Timeline & Lab Intelligence (Real Data) */}
          <div className="bg-[#040d1a]/80 backdrop-blur-xl border border-white/10 rounded-3xl p-6">
            <h4 className="text-lg font-bold text-white flex items-center gap-2 mb-6">
              <Clock className="w-5 h-5 text-indigo-400" /> Clinical & Operational Timeline
            </h4>
            
            <div className="relative pl-6 space-y-6 before:content-[''] before:absolute before:left-[11px] before:top-2 before:bottom-2 before:w-[2px] before:bg-indigo-500/20">
              
              {/* Admission */}
              <div className="relative">
                <div className="absolute -left-[30px] top-1 w-3 h-3 rounded-full bg-indigo-500 border-4 border-[#040d1a]" />
                <p className="text-xs text-indigo-400 font-bold tracking-wider uppercase mb-1">Admission <span className="text-gray-600 font-normal ml-2">Source: HIS</span></p>
                <p className="text-sm text-gray-300">Patient admitted on {new Date(patient.admission_datetime).toLocaleString()}</p>
              </div>

              {/* Labs */}
              {patient.lab_results.map((lab, i) => (
                <div key={i} className="relative">
                  <div className={`absolute -left-[30px] top-1 w-3 h-3 rounded-full border-4 border-[#040d1a] ${lab.result_status === "Completed" ? "bg-teal-500" : "bg-amber-500"}`} />
                  <p className="text-xs text-indigo-400 font-bold tracking-wider uppercase mb-1">Lab Order <span className="text-gray-600 font-normal ml-2">Source: LIS</span></p>
                  <div className="bg-white/5 border border-white/5 rounded-xl p-3">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-white">{lab.test_name}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded uppercase tracking-wider font-bold ${lab.result_status === "Completed" ? "bg-teal-500/20 text-teal-400" : "bg-amber-500/20 text-amber-400"}`}>
                        {lab.result_status}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>Ordered: {new Date(lab.order_datetime).toLocaleTimeString()}</span>
                      {lab.result_status === "Completed" ? (
                        <span className="text-teal-500/70">TAT: {lab.tat_minutes}m</span>
                      ) : (
                        <span className="text-amber-500/70">Pending...</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Conflict */}
              {patient.has_conflict && (
                <div className="relative">
                  <div className="absolute -left-[30px] top-1 w-3 h-3 rounded-full bg-rose-500 border-4 border-[#040d1a]" />
                  <p className="text-xs text-rose-400 font-bold tracking-wider uppercase mb-1">Reconciliation Conflict <span className="text-gray-600 font-normal ml-2">Source: HospOps Engine</span></p>
                  <p className="text-sm text-gray-300">Data collision detected requiring operational resolution.</p>
                </div>
              )}

            </div>
          </div>

        </div>
      </div>

      {/* Why Flagged Explainability Modal */}
      {showWhyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-[#071428] border border-indigo-500/30 rounded-3xl max-w-lg w-full p-6 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-rose-500" />
            <h3 className="text-xl font-bold text-white mb-4">Explainability Engine</h3>
            
            <div className="space-y-4">
              <div className="bg-white/5 p-4 rounded-xl border border-white/5">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Source Dataset</p>
                <p className="text-sm text-white font-mono">Reconciliation Engine & Laboratory IS</p>
              </div>
              <div className="bg-white/5 p-4 rounded-xl border border-white/5">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Configured Rule</p>
                <p className="text-sm text-amber-200">"Flag patients with STAT labs pending &gt; 60 mins OR active operational desyncs."</p>
              </div>
              <div className="bg-white/5 p-4 rounded-xl border border-white/5">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Conclusion</p>
                <p className="text-sm text-white">Clinical/Operational review recommended to prevent delay in care.</p>
              </div>
            </div>

            <button onClick={() => setShowWhyModal(false)} className="mt-6 w-full py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all">
              Acknowledge
            </button>
          </div>
        </div>
      )}

    </div>
  );
}
